import { z } from 'zod';

import { TCmsTypeSlug } from './dbConstants';
import { FeedbackAction, GameType, LeaderboardAction } from './dbEnums';

///
/// This file contains strongly-typed JSON models that are used by the DB schemas and also have to appear in API
/// contracts that the front end uses. These zod models will end up in the frontend bundle.
///

export const ZPromoConditions = z.object({
  isftmp: z.boolean(),
  isSubscribed: z.boolean(),
  drawTypeCode: z.string(),
  planCode: z.string(),
  planSubcode: z.string(),
  numEntries: z.number(),
  scheduleCode: z.string(),
});
export type TPromoConditions = z.infer<typeof ZPromoConditions>;

export const ZDrawTypeNumberSetup = z.object({
  type: z.literal(GameType.LOTTERY),
  main: z.number().array(),
  bonus: z.number().array(),
});

export const ZLotteryDrawEntry = z.object({
  type: z.literal(GameType.LOTTERY),
  main: z.number().array(),
  bonus: z.number().array(),
});

export const ZRaffleEntry = z.object({
  type: z.literal(GameType.RAFFLE),
  raffle: z.string(),
});

export const ZBigRaffleEntry = z.object({
  type: z.literal(GameType.BIG_RAFFLE),
  weight: z.number(),
});

export const ZLeaderboardEntry = z.object({
  type: z.literal(GameType.LEADERBOARD),
  points: z.number(),
  action: z.nativeEnum(LeaderboardAction),
  raffle: z.string(),
});

export const ZEntryLine = z.union([
  ZLotteryDrawEntry,
  ZRaffleEntry,
  ZBigRaffleEntry,
  ZLeaderboardEntry,
]);

export type TDrawTypeNumberSetup = z.infer<typeof ZDrawTypeNumberSetup>;
export type TLotteryDrawEntry = z.infer<typeof ZLotteryDrawEntry>;

export type TRaffleDrawEntry = z.infer<typeof ZRaffleEntry>;
export type TBigRaffleDrawEntry = z.infer<typeof ZBigRaffleEntry>;
export type TLeaderboardEntry = z.infer<typeof ZLeaderboardEntry>;

export type TEntryLine = z.infer<typeof ZEntryLine>;

const ZAdditionalInfo = z.object({
  result: z.coerce.number().array(),
  verificationPageLink: z.string(),
  serialNumber: z.coerce.number(),
  entriesHash: z.string().optional(),
});

const ZRaffleResultAdditionalInfo = z.object({
  playerId: z.string().optional(),
  entriesHash: z.string().optional(),
  resultNumber: z.number().optional(),
  verificationPageLink: z.string().optional(),
  serialNumber: z.number().optional(),
});

const ZBigRaffleResultAdditionalInfo = z.object({
  playerId: z.string(),
  entriesHash: z.string().optional(),
  resultNumber: z.number().optional(),
  verificationPageLink: z.string().optional(),
  serialNumber: z.number().optional(),
});

export const ZLeaderboardResultAdditionalInfo = z.object({
  raffleWinner: z.object({
    playerId: z.string(),
  }),
  leaderPos1: z.object({
    playerId: z.string(),
    points: z.number(),
  }),
  leaderPos2: z.object({
    playerId: z.string(),
    points: z.number(),
  }),
  leaderPos3: z.object({
    playerId: z.string(),
    points: z.number(),
  }),
});

export const ZLotteryDrawResult = z.object({
  type: z.literal(GameType.LOTTERY),
  main: z.number().array(),
  bonus: z.number().array(),
  mainAdditionalInfo: z.optional(ZAdditionalInfo),
  bonusAdditionalInfo: z.optional(ZAdditionalInfo),
});

export const ZRaffleResult = z.object({
  type: z.literal(GameType.RAFFLE),
  raffle: z.string().nullable(),
  raffleAdditionalInfo: z.optional(ZRaffleResultAdditionalInfo),
});

export const ZBigRaffleResult = z.object({
  type: z.literal(GameType.BIG_RAFFLE),
  ticketEntryId: z.string().nullable(),
  bigRaffleAdditionalInfo: z.optional(ZBigRaffleResultAdditionalInfo),
});

export const ZLeaderboardResult = z.object({
  type: z.literal(GameType.LEADERBOARD),
  raffle: z.string().nullable(),
  leaderboardAdditionalInfo: z.optional(ZLeaderboardResultAdditionalInfo),
});

export type TLotteryDrawResult = z.infer<typeof ZLotteryDrawResult>;

export type TRaffleDrawResult = z.infer<typeof ZRaffleResult>;
export type TBigRaffleResult = z.infer<typeof ZBigRaffleResult>;
export type TLeaderboardResult = z.infer<typeof ZLeaderboardResult>;

export const ZDrawTypeFullResult = z.union([
  ZLotteryDrawResult,
  ZRaffleResult,
  ZBigRaffleResult,
  ZLeaderboardResult,
]);

export type TDrawTypeFullResult = z.infer<typeof ZDrawTypeFullResult>;

//Record<string, Record<string, string>>

export const ZSimulatedWinners = z.record(
  z.string(),
  z.record(z.string(), z.string()),
);

export type TSimulatedWinners = z.infer<typeof ZSimulatedWinners>;

export const ZPriceTable = z.record(
  z.union([
    z.record(z.literal('GBP'), z.record(z.number(), z.number())),
    z.record(z.literal('TOKEN'), z.record(z.number(), z.number())),
  ]),
);
export type TPriceTable = z.infer<typeof ZPriceTable>;

export type priceTableType = Record<
  string,
  | Record<'GBP', Record<number, number>>
  | Record<'TOKEN', Record<number, number>>
>;

export const ZFeedbackContent = z.union([
  z.object({
    [FeedbackAction.CLAIM_PRIZE_WIN]: z.object({
      rating: z.number(),
      comment: z.string().optional(),
    }),
  }),
  z.object({
    [FeedbackAction.CANCEL_SUBSCRIPTION]: z.object({
      comment: z.string(),
    }),
  }),
  z.object({
    [FeedbackAction.TOKEN_TOWN]: z.object({
      comment: z.string(),
    }),
  }),
  z.object({
    /**  TODO: ENG-85 */
    [FeedbackAction.SUGGEST_PRIZE]: z.object({
      comment: z.string(),
    }),
  }),
]);

export type TFeedbackContent = z.infer<typeof ZFeedbackContent>;

export const ZSubscriptionHistoryAdditionalInfo = z.record(
  z.string(),
  z.unknown(),
);
export type TSubscriptionHistoryAdditionalInfo = z.infer<
  typeof ZSubscriptionHistoryAdditionalInfo
>;

export const ZPlayerABTestVariant = z.record(z.string(), z.string());

export const ZOrderAttributionParams = z.record(z.string(), z.string());
export type TOrderAttributionParams = z.infer<typeof ZOrderAttributionParams>;

export const ZOrderUtmParams = z
  .record(
    z.string(),
    // We have database rows that have a null value in their order.utm_params record
    z.union([z.string().array(), z.string().nullish()]),
  )
  .nullish();
export type TOrderUtmParams = z.infer<typeof ZOrderUtmParams>;

export const ZPrizeImageLocationsBase = z.object({
  main: z.string(),
  secondary1: z.string().nullish(),
  secondary2: z.string().nullish(),
});
export type TPrizeImageLocationsBase = z.infer<typeof ZPrizeImageLocationsBase>;

export const ZPrizeImageLocations = ZPrizeImageLocationsBase.extend({
  // temporary fields for additional images until we revamp image storage properly
  gallery0: z.string().nullish(),
  gallery1: z.string().nullish(),
  gallery2: z.string().nullish(),
  gallery3: z.string().nullish(),
  gallery4: z.string().nullish(),
  gallery5: z.string().nullish(),
  gallery6: z.string().nullish(),
  gallery7: z.string().nullish(),
  gallery8: z.string().nullish(),
  gallery9: z.string().nullish(),
});
export type TPrizeImageLocations = z.infer<typeof ZPrizeImageLocations>;

export const ZEmailBrevoData = z.array(
  z.object({
    status: z.string(),
    date: z.coerce.date(),
  }),
);
export type TEmailBrevoData = z.infer<typeof ZEmailBrevoData>;

// ─── CMS field configuration ───────────────────────────────────────────────────
// Pure-data config (no React). Single source of truth for field names, labels,
// editor type, and required flags. Zod schemas are derived from these configs.

export type CmsEditorType =
  | 'string'
  | 'url'
  | 'date'
  | 'image'
  | 'html'
  | 'boolean'
  | 'int'
  | 'decimal'
  | 'enum'
  | 'json';

export type CmsFieldMeta = {
  label: string;
  required?: boolean;
  editor: CmsEditorType;
  enumOptions?: readonly string[];
};

// Maps each editor type to its Zod value schema (no React, no UI)
const CMS_EDITOR_ZOD = {
  string: z.string(),
  url: z.string(),
  date: z.string(),
  image: z.string().nullable(),
  html: z.string(),
  boolean: z.boolean(),
  int: z.number().int().nullable(),
  decimal: z.number().nullable(),
  enum: z.string(),
  json: z.unknown().nullable(),
} as const satisfies Record<CmsEditorType, z.ZodTypeAny>;

function buildZodSchema<
  T extends Record<string, { editor: keyof typeof CMS_EDITOR_ZOD }>,
>(fields: T) {
  return z.object(
    Object.fromEntries(
      Object.entries(fields).map(([k, f]) => [k, CMS_EDITOR_ZOD[f.editor]]),
    ),
  ) as z.ZodObject<{
    [K in keyof T]: (typeof CMS_EDITOR_ZOD)[T[K]['editor']];
  }>;
}

export type TCmsTypeConfig = {
  slug: TCmsTypeSlug;
  name: string;
  fields: Record<string, CmsFieldMeta>;
};

// ─── Winner ────────────────────────────────────────────────────────────────────

export const WinnerConfig = {
  slug: 'winner',
  name: 'Winner',
  fields: {
    winnerName: {
      label: 'Winner Name',
      required: true,
      editor: 'string',
    },
    prizeName: {
      label: 'Prize Name',
      required: true,
      editor: 'string',
    },
    prizeSlug: { label: 'Prize Slug', editor: 'string' },
    title: { label: 'Title', editor: 'string' },
    winDate: { label: 'Win Date', editor: 'date' },
    image: { label: 'Winner Image', required: true, editor: 'image' },
    videoLink: { label: 'Video Link', editor: 'url' },
  },
} as const satisfies TCmsTypeConfig;

export const ZWinnerDocumentData = buildZodSchema(WinnerConfig.fields);
export type TWinnerDocumentData = z.infer<typeof ZWinnerDocumentData>;

// ─── WinnerPolaroid ─────────────────────────────────────────────────────────────

export const WinnerPolaroidConfig = {
  slug: 'winner-polaroid',
  name: 'Winner (Polaroid)',
  fields: {
    winnerName: {
      label: 'Winner Name',
      required: true,
      editor: 'string',
    },
    prizeName: {
      label: 'Prize Name',
      editor: 'string',
    },
    image: { label: 'Winner Image', required: true, editor: 'image' },
  },
} as const satisfies TCmsTypeConfig;

export const ZWinnerPolaroidDocumentData = buildZodSchema(
  WinnerPolaroidConfig.fields,
);
export type TWinnerPolaroidDocumentData = z.infer<
  typeof ZWinnerPolaroidDocumentData
>;

// ─── Review ────────────────────────────────────────────────────────────────────

export const ReviewConfig = {
  slug: 'review',
  name: 'Review',
  fields: {
    author: { label: 'Author', required: true, editor: 'string' },
    reviewDate: { label: 'Review Date', editor: 'date' },
    title: { label: 'Title', editor: 'string' },
    content: { label: 'Content', required: true, editor: 'html' },
  },
} as const satisfies TCmsTypeConfig;

export const ZReviewDocumentData = buildZodSchema(ReviewConfig.fields);
export type TReviewDocumentData = z.infer<typeof ZReviewDocumentData>;

/** Union of all known CMS document data shapes (all fields partial — JSONB may have gaps in older records). */
export const ZCmsDocumentData = z.union([
  /** must use .strict() otherwise any object will match the first type and have the rest of its keys stripped */
  ZWinnerDocumentData.partial().strict(),
  ZWinnerPolaroidDocumentData.partial().strict(),
  ZReviewDocumentData.partial().strict(),
]);
export type TCmsDocumentData = z.infer<typeof ZCmsDocumentData>;

export const CmsTypeSlugToZDocumentData = {
  winner: ZWinnerDocumentData.partial(),
  'winner-polaroid': ZWinnerPolaroidDocumentData.partial(),
  review: ZReviewDocumentData.partial(),
} as const satisfies Record<TCmsTypeSlug, z.ZodTypeAny>;
export type CmsTypeSlugToTDocumentData = {
  [K in keyof typeof CmsTypeSlugToZDocumentData]: Partial<
    z.infer<(typeof CmsTypeSlugToZDocumentData)[K]>
  >;
};
